import React, { useEffect, useState, useCallback } from 'react';
import {
  Table, Input, Modal, Form, message, Button, Breadcrumb, Grid, Card, Row, Col,
  Statistic, Tag, Tooltip, Switch, Space, Badge, DatePicker
} from 'antd';
import { vehicleBrandsService } from '../../common/services';
import {
  EditOutlined, DeleteOutlined, RightOutlined, SearchOutlined,
  CarOutlined, CalendarOutlined, PlusOutlined, CheckOutlined,
  CloseOutlined, EyeOutlined, ImportOutlined, DatabaseOutlined
} from '@ant-design/icons';
import { Link, useNavigate } from 'react-router-dom';
import dayjs from 'dayjs';

const VehicleBrands = () => {
  const navigate = useNavigate();
  const [brands, setBrands] = useState([]);
  const [isLoading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [dateRange, setDateRange] = useState(null);
  const [stats, setStats] = useState({ total: 0, active: 0, inactive: 0 });

  // Modal d'ajout/édition
  const [isModalOpen, setModalOpen] = useState(false);
  const [selectedBrand, setSelectedBrand] = useState(null);
  const [form] = Form.useForm();

  // Modal d'import
  const [isImportModalOpen, setImportModalOpen] = useState(false);
  const [importText, setImportText] = useState('');
  const [importLoading, setImportLoading] = useState(false);

  const screens = Grid.useBreakpoint();
  const isMobile = !screens.sm;
  const controlSize = isMobile ? 'small' : 'middle';

  const calculateStats = useCallback((data) => {
    const active = data.filter(b => b.isActive !== false).length;
    setStats({
      total: data.length,
      active,
      inactive: data.length - active
    });
  }, []);

  useEffect(() => {
    fetchBrands();
  }, []);

  const fetchBrands = async () => {
    setLoading(true);
    try {
      const data = await vehicleBrandsService.getBrands();
      const brandsData = Array.isArray(data) ? data : [];
      setBrands(brandsData);
      calculateStats(brandsData);
    } catch (err) {
      console.error('Erreur:', err);
      message.error('Erreur lors du chargement des marques');
    } finally {
      setLoading(false);
    }
  };

  const filteredBrands = brands.filter((brand) => {
    if (!brand.name?.toLowerCase().includes(searchTerm.toLowerCase())) return false;
    if (dateRange && dateRange[0] && dateRange[1]) {
      const created = dayjs(brand.createdAt);
      return created.isAfter(dateRange[0].startOf('day').subtract(1, 'ms')) &&
        created.isBefore(dateRange[1].endOf('day').add(1, 'ms'));
    }
    return true;
  });

  const handleAdd = () => {
    setSelectedBrand(null);
    form.resetFields();
    setModalOpen(true);
  };

  const handleEdit = (record) => {
    setSelectedBrand(record);
    form.setFieldsValue({ name: record.name });
    setModalOpen(true);
  };

  const handleDelete = (record) => {
    Modal.confirm({
      title: 'Supprimer la marque',
      content: (
        <div>
          <p>Voulez-vous vraiment supprimer la marque <strong>{record.name}</strong> ?</p>
          {record.modelCount > 0 && (
            <Tag color="red">
              Attention: {record.modelCount} modèle(s) associé(s). Supprimez d'abord les modèles.
            </Tag>
          )}
        </div>
      ),
      okText: 'Supprimer',
      okType: 'danger',
      cancelText: 'Annuler',
      onOk: async () => {
        try {
          const response = await vehicleBrandsService.deleteBrand(record._id);
          if (response?.success) {
            message.success('Marque supprimée');
            fetchBrands();
          } else {
            message.error(response?.message || 'Erreur lors de la suppression');
          }
        } catch (error) {
          message.error(error?.message || 'Erreur lors de la suppression');
        }
      },
    });
  };

  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();

      if (selectedBrand) {
        const response = await vehicleBrandsService.updateBrand(selectedBrand._id, values);
        if (response?.success) {
          message.success('Marque mise à jour');
          setModalOpen(false);
          fetchBrands();
        } else {
          message.error(response?.message || 'Erreur');
        }
      } else {
        const response = await vehicleBrandsService.addBrand(values);
        if (response?.success) {
          message.success('Marque créée');
          setModalOpen(false);
          fetchBrands();
        } else {
          message.error(response?.message || 'Erreur');
        }
      }
    } catch (error) {
      message.error(error?.message || 'Erreur');
    }
  };

  const handleToggle = async (record, checked) => {
    try {
      const response = await vehicleBrandsService.toggleBrand(record._id, checked);
      if (response?.success) {
        message.success(checked ? 'Marque activée' : 'Marque désactivée');
        fetchBrands();
      } else {
        message.error(response?.message || 'Erreur');
      }
    } catch (error) {
      message.error(error?.message || 'Erreur');
    }
  };

  const handleImport = async () => {
    if (!importText.trim()) {
      message.error('Veuillez entrer des marques');
      return;
    }

    setImportLoading(true);
    try {
      const brandsList = importText.split('\n')
        .map(b => b.trim())
        .filter(b => b.length > 0);

      const response = await vehicleBrandsService.importBrands(brandsList);
      if (response?.success) {
        message.success(response.message);
        setImportModalOpen(false);
        setImportText('');
        fetchBrands();
      } else {
        message.error(response?.message || 'Erreur');
      }
    } catch (error) {
      message.error(error?.message || 'Erreur');
    } finally {
      setImportLoading(false);
    }
  };

  const handleSeed = async () => {
    Modal.confirm({
      title: 'Initialiser les marques et modèles',
      content: (
        <div>
          <p>Cette action va créer toutes les marques et modèles prédéfinis.</p>
          <p style={{ color: '#666' }}>Les marques/modèles existants seront ignorés.</p>
        </div>
      ),
      okText: 'Initialiser',
      cancelText: 'Annuler',
      onOk: async () => {
        const hideLoading = message.loading('Initialisation en cours...', 0);
        try {
          const response = await vehicleBrandsService.seed();
          hideLoading();
          if (response?.success) {
            message.success(response.message);
            fetchBrands();
          } else {
            message.error(response?.message || 'Erreur');
          }
        } catch (error) {
          hideLoading();
          message.error(error?.message || 'Erreur');
        }
      },
    });
  };

  const columns = [
    {
      title: 'Marque',
      key: 'name',
      render: (_, record) => (
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <CarOutlined style={{ fontSize: 24, color: '#fa8c16' }} />
          <div>
            <div style={{ fontWeight: 600, fontSize: 15 }}>{record.name}</div>
            <Badge
              count={`${record.modelCount || 0} modèle(s)`}
              style={{ backgroundColor: record.modelCount > 0 ? '#52c41a' : '#d9d9d9' }}
            />
          </div>
        </div>
      ),
      sorter: (a, b) => a.name.localeCompare(b.name),
    },
    {
      title: 'Date de création',
      dataIndex: 'createdAt',
      key: 'createdAt',
      width: 180,
      render: (date) => (
        <span style={{ color: '#595959' }}>
          <CalendarOutlined style={{ marginRight: 6, color: '#fa8c16' }} />
          {date}
        </span>
      )
    },
    {
      title: 'Statut',
      key: 'status',
      width: 100,
      align: 'center',
      render: (_, record) => (
        <Tooltip title={record.isActive !== false ? 'Active' : 'Inactive'}>
          <Switch
            checked={record.isActive !== false}
            onChange={(checked) => handleToggle(record, checked)}
            checkedChildren={<CheckOutlined />}
            unCheckedChildren={<CloseOutlined />}
            size="small"
          />
        </Tooltip>
      )
    },
    {
      title: 'Actions',
      key: 'actions',
      width: 150,
      align: 'center',
      render: (_, record) => (
        <Space>
          <Tooltip title="Voir les modèles">
            <Button
              icon={<EyeOutlined />}
              size="small"
              onClick={() => navigate(`/dashboard/vehicle-brands/${record._id}/models`)}
              style={{ borderRadius: 6, color: '#1890ff', borderColor: '#1890ff' }}
            />
          </Tooltip>
          <Tooltip title="Modifier">
            <Button
              icon={<EditOutlined />}
              size="small"
              onClick={() => handleEdit(record)}
              style={{ borderRadius: 6, color: '#faad14', borderColor: '#faad14' }}
            />
          </Tooltip>
          <Tooltip title="Supprimer">
            <Button
              danger
              type="text"
              icon={<DeleteOutlined />}
              size="small"
              onClick={() => handleDelete(record)}
              disabled={record.modelCount > 0}
            />
          </Tooltip>
        </Space>
      ),
    },
  ];

  return (
    <div className="content-wrapper">
      <style>
        {`
          .stat-card-orange {
            background: linear-gradient(135deg, #fa8c16 0%, #ffc069 100%);
            border-radius: 12px;
            border: none;
          }
          .stat-card-orange .ant-statistic-title,
          .stat-card-orange .ant-statistic-content {
            color: white !important;
          }
        `}
      </style>

      <div className="container-xxl flex-grow-1" style={{ paddingTop: 8, paddingLeft: 24, paddingRight: 24 }}>
        <Breadcrumb
          separator={<RightOutlined style={{ fontSize: 12, color: '#8c8c8c' }} />}
          style={{ marginBottom: 12 }}
        >
          <Breadcrumb.Item>
            <Link to="/dashboard" style={{ color: '#fa8c16' }}>Accueil</Link>
          </Breadcrumb.Item>
          <Breadcrumb.Item>
            <span style={{ color: '#595959' }}>Marques & Modèles</span>
          </Breadcrumb.Item>
        </Breadcrumb>

        {/* Statistiques */}
        <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
          <Col xs={8}>
            <Card className="stat-card-orange" bodyStyle={{ padding: isMobile ? 12 : 16 }}>
              <Statistic
                title={<span><CarOutlined style={{ marginRight: 6 }} />Total</span>}
                value={stats.total}
                valueStyle={{ fontSize: isMobile ? 20 : 28, fontWeight: 'bold' }}
              />
            </Card>
          </Col>
          <Col xs={8}>
            <Card className="stat-card-orange" bodyStyle={{ padding: isMobile ? 12 : 16 }}>
              <Statistic
                title={<span><CheckOutlined style={{ marginRight: 6 }} />Actives</span>}
                value={stats.active}
                valueStyle={{ fontSize: isMobile ? 20 : 28, fontWeight: 'bold' }}
              />
            </Card>
          </Col>
          <Col xs={8}>
            <Card className="stat-card-orange" bodyStyle={{ padding: isMobile ? 12 : 16 }}>
              <Statistic
                title={<span><CloseOutlined style={{ marginRight: 6 }} />Inactives</span>}
                value={stats.inactive}
                valueStyle={{ fontSize: isMobile ? 20 : 28, fontWeight: 'bold' }}
              />
            </Card>
          </Col>
        </Row>

        {/* Carte principale */}
        <Card style={{ borderRadius: 16, boxShadow: '0 2px 8px rgba(0,0,0,0.08)' }}>
          <Row gutter={[12, 12]} style={{ marginBottom: 20 }}>
            <Col xs={24} sm={8}>
              <Input
                prefix={<SearchOutlined style={{ color: '#fa8c16' }} />}
                placeholder="Rechercher une marque..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                allowClear
                size={controlSize}
                style={{ borderRadius: 8 }}
              />
            </Col>
            <Col xs={24} sm={8}>
              <DatePicker.RangePicker
                value={dateRange}
                onChange={(v) => setDateRange(v)}
                format="DD/MM/YYYY"
                placeholder={['Date début', 'Date fin']}
                style={{ width: '100%', borderRadius: 8 }}
                size={controlSize}
                allowClear
              />
            </Col>
            <Col xs={24} sm={8} style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, flexWrap: 'wrap' }}>
              <Button
                icon={<DatabaseOutlined />}
                onClick={handleSeed}
                size={controlSize}
                type="dashed"
              >
                Initialiser
              </Button>
              <Button
                icon={<ImportOutlined />}
                onClick={() => setImportModalOpen(true)}
                size={controlSize}
              >
                Import
              </Button>
              <Button
                type="primary"
                icon={<PlusOutlined />}
                onClick={handleAdd}
                size={controlSize}
                style={{
                  background: 'linear-gradient(135deg, #fa8c16 0%, #ffc069 100%)',
                  border: 'none'
                }}
              >
                Ajouter une marque
              </Button>
            </Col>
          </Row>

          <Table
            columns={columns}
            dataSource={filteredBrands.map((brand) => ({ ...brand, key: brand._id }))}
            pagination={{ pageSize: 15, showSizeChanger: true }}
            loading={isLoading}
            size={controlSize}
            scroll={{ x: 600 }}
          />
        </Card>
      </div>

      {/* Modal Ajout/Édition */}
      <Modal
        title={selectedBrand ? 'Modifier la marque' : 'Ajouter une marque'}
        open={isModalOpen}
        onCancel={() => setModalOpen(false)}
        onOk={handleSubmit}
        okText={selectedBrand ? 'Modifier' : 'Ajouter'}
        cancelText="Annuler"
      >
        <Form form={form} layout="vertical">
          <Form.Item
            label="Nom de la marque"
            name="name"
            rules={[{ required: true, message: 'Le nom est requis' }]}
          >
            <Input placeholder="Ex: Toyota, Suzuki..." />
          </Form.Item>
        </Form>
      </Modal>

      {/* Modal Import */}
      <Modal
        title="Importer des marques"
        open={isImportModalOpen}
        onCancel={() => setImportModalOpen(false)}
        onOk={handleImport}
        okText="Importer"
        cancelText="Annuler"
        confirmLoading={importLoading}
      >
        <p style={{ color: '#666', marginBottom: 16 }}>
          Entrez une marque par ligne. Les marques existantes seront ignorées.
        </p>
        <Input.TextArea
          rows={10}
          placeholder="Toyota&#10;Suzuki&#10;Honda&#10;..."
          value={importText}
          onChange={(e) => setImportText(e.target.value)}
        />
      </Modal>
    </div>
  );
};

export default VehicleBrands;
